/** @jsx jsx */
import { useBreakpointIndex } from "@theme-ui/match-media";
import { FC } from "react";
import { Composite, useCompositeState } from "reakit/Composite";
import { jsx } from "theme-ui";

import { TimeReport, TimeReportAggregateReturn } from "@/data/time-reports";

import { TimeReportCalendarGridContext } from "./grid/TimeReportCalendarGridContext";
import { TimeReportCalendarHeaderRow } from "./TimeReportCalendarHeaderRow";
import { TimeReportCalendarProjectRowGroup } from "./TimeReportCalendarProjectRowGroup";
import { TimeReportCalendarSummaryRow } from "./TimeReportCalendarSummaryRow";

export interface TimeReportCalendarGridProps {
  timeReport: TimeReport;
  aggregateData: TimeReportAggregateReturn;
  editable?: boolean;
}

export const TimeReportCalendarGrid: FC<TimeReportCalendarGridProps> = ({
  timeReport,
  aggregateData,
  editable = false,
  ...restProps
}) => {
  const compositeState = useCompositeState({ shift: true });

  const breakpoint = useBreakpointIndex();

  return (
    <TimeReportCalendarGridContext.Provider value={{ compositeState, isEditable: editable }}>
      <Composite
        {...compositeState}
        {...restProps}
        as="div"
        role="treegrid"
        aria-label="grid"
        sx={{
          display: "table",
          tableLayout: "fixed",
          borderCollapse: "collapse",

          "& :focus": {
            outlineStyle: "auto",
            outlineWidth: 1,
            outlineColor: "n700",
          },
        }}>
        {breakpoint >= 3 && <TimeReportCalendarHeaderRow days={aggregateData.days} />}

        {aggregateData.projects.map((project, idx) => (
          <TimeReportCalendarProjectRowGroup
            key={project.project.project.guid ?? `p-${idx}`}
            timeReport={timeReport}
            project={project}
            aggregateData={aggregateData}
            editable={editable}
          />
        ))}

        <TimeReportCalendarSummaryRow aggregateData={aggregateData} />
      </Composite>
    </TimeReportCalendarGridContext.Provider>
  );
};
